import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import {
  ArrowRight,
  ArrowUpRight,
  Compass,
  Download,
  FileText,
  Hammer,
  Landmark,
  Mail,
  ShieldCheck,
  ChevronRight,
  BookOpen,
  Building2,
  Clock,
  ChevronUp,
} from "lucide-react";
import { WATCH_TRACKS } from "../lib/watchTracks";

/**
 * /resources — Editorial reference library for WHOLE Neighborhoods.
 *
 * One scroll, eight anchored sections, role-aware "open your section"
 * affordance for signed-in visitors. No role gating — every audience can
 * read every section. Designed for the LISC Indianapolis demo with
 * substantive content (not lorem ipsum), real Indianapolis specifics, and
 * working in-app links to the marketplace / sign-in / register paths.
 *
 * Composition rules (matches the rest of the platform):
 *   • Fraunces section titles, Newsreader italic accents, Nunito Sans body
 *   • Hairline dividers (#D9D8E4 / #E8E7EE), no glassmorphism, no gradients
 *   • Sticky TOC on lg screens, inline TOC on mobile
 *   • Smooth-scroll anchored sections with active-section highlight
 *   • `tabular-nums` for read-time and code references
 */

const SECTIONS = [
  { id: "owners",     label: "For Owners & CDCs",    minutes: 5, eyebrow: "Audience" },
  { id: "architects", label: "For Architects",        minutes: 6, eyebrow: "Audience" },
  { id: "city",       label: "For City Staff",        minutes: 4, eyebrow: "Audience" },
  { id: "watch",      label: "Watch",                 minutes: 12, eyebrow: "Field guide" },
  { id: "glossary",   label: "Civic Glossary",        minutes: 3, eyebrow: "Reference" },
  { id: "code",       label: "Code & Standards",      minutes: 4, eyebrow: "Reference" },
  { id: "downloads",  label: "Downloads & Templates", minutes: 1, eyebrow: "Reference" },
  { id: "help",       label: "Help & Contact",        minutes: 1, eyebrow: "Reference" },
];

const ROLE_TO_SECTION = {
  owner_builder: "owners",
  architect: "architects",
  city_staff: "city",
  platform_admin: "city",
};

export default function Resources() {
  const { user, role } = useRole();
  const tailored = ROLE_TO_SECTION[role];
  const tailoredLabel = SECTIONS.find((s) => s.id === tailored)?.label;
  const [active, setActive] = useState(SECTIONS[0].id);

  // Active section tracker — highlights the current TOC item as the user
  // scrolls through the page. Uses IntersectionObserver with a 25%
  // viewport margin so the next section becomes active when its title
  // crosses a quarter of the way up.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-25% 0px -55% 0px", threshold: [0, 0.3, 0.7, 1] },
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section data-testid="resources-page" className="animate-fade-in-up">
      <Hero
        tailored={tailored}
        tailoredLabel={tailoredLabel}
        userName={user?.name}
        onOpenTailored={() => tailored && scrollTo(tailored)}
      />

      <div
        className="mt-12 grid gap-10"
        style={{ gridTemplateColumns: "minmax(0, 1fr) 280px" }}
      >
        <div className="min-w-0">
          <SectionOwners onCta={scrollTo} />
          <Divider />
          <SectionArchitects onCta={scrollTo} />
          <Divider />
          <SectionCity onCta={scrollTo} />
          <Divider />
          <SectionWatch />
          <Divider />
          <SectionGlossary />
          <Divider />
          <SectionCode />
          <Divider />
          <SectionDownloads />
          <Divider />
          <SectionHelp />
        </div>

        <aside
          className="hidden lg:block"
          style={{ alignSelf: "start", position: "sticky", top: 96 }}
        >
          <TableOfContents active={active} onJump={scrollTo} />
        </aside>
      </div>

      <BackToTop />
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Hero                                                                    */
/* ─────────────────────────────────────────────────────────────────────── */

function Hero({ tailored, tailoredLabel, userName, onOpenTailored }) {
  return (
    <header data-testid="resources-hero">
      <div className="flex items-center gap-3">
        <span aria-hidden style={{ display: "inline-block", width: 32, height: 2, background: "#B14E2F" }} />
        <span className="wn-label" style={{ color: "#8F3C23" }}>
          WHOLE Reference Library
        </span>
      </div>
      <h1
        className="mt-3"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: "clamp(34px, 4.6vw, 52px)",
          fontWeight: 500,
          lineHeight: 1.05,
          letterSpacing: "-0.02em",
          maxWidth: 840,
        }}
        data-testid="resources-title"
      >
        Everything you need to{" "}
        <span style={{ fontStyle: "italic", color: "#8F3C23" }}>
          build, review, or browse
        </span>{" "}
        on WHOLE.
      </h1>
      <p
        className="mt-6"
        style={{
          color: "#3D3D55",
          fontSize: 17,
          lineHeight: 1.65,
          maxWidth: 720,
          fontFamily: '"Newsreader", Georgia, serif',
        }}
      >
        A field guide to pre-reviewed infill housing in Indianapolis. How
        the marketplace works, what we look for in an architect, how the
        City reviews a design, and the vocabulary every audience needs to
        speak the same language.
      </p>

      {tailored && (
        <div
          className="mt-7 wn-card flex flex-wrap items-center gap-4"
          style={{
            padding: "14px 20px",
            background: "#FBFAFD",
            border: "1px solid #D9D8E4",
            maxWidth: 720,
          }}
          data-testid="resources-tailored-pill"
        >
          <div
            aria-hidden
            style={{
              width: 36,
              height: 36,
              borderRadius: 999,
              background: "rgba(177,78,47,0.10)",
              color: "#8F3C23",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <BookOpen size={17} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="wn-label" style={{ color: "#8F3C23" }}>
              Tailored for you
            </div>
            <div
              className="mt-0.5"
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                color: "#1B1F4B",
                fontSize: 15,
                fontWeight: 600,
              }}
            >
              {userName ? `${userName} — ` : ""}jump to {tailoredLabel}.
            </div>
          </div>
          <button
            type="button"
            onClick={onOpenTailored}
            className="wn-btn wn-btn-primary inline-flex items-center"
            style={{ gap: 6, fontSize: 12 }}
            data-testid="resources-tailored-cta"
          >
            Open your section <ArrowRight size={13} />
          </button>
        </div>
      )}

      {/* Inline TOC (mobile + medium screens) */}
      <div className="lg:hidden mt-9">
        <TableOfContents inline />
      </div>
    </header>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Table of Contents                                                       */
/* ─────────────────────────────────────────────────────────────────────── */

function TableOfContents({ active, onJump, inline = false }) {
  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    onJump && onJump(id);
  };
  return (
    <nav
      aria-label="Resources sections"
      style={{
        border: "1px solid #D9D8E4",
        background: "#FBFAFD",
        padding: 20,
        borderRadius: 4,
      }}
      data-testid={inline ? "resources-toc-inline" : "resources-toc"}
    >
      <div className="wn-label" style={{ color: "#8F3C23", marginBottom: 12 }}>
        Contents
      </div>
      <ol style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {SECTIONS.map((s, i) => {
          const isActive = !inline && active === s.id;
          return (
            <li
              key={s.id}
              style={{
                borderTop: i === 0 ? "none" : "1px solid #E8E7EE",
              }}
            >
              <a
                href={`#${s.id}`}
                onClick={(e) => handleClick(e, s.id)}
                data-testid={
                  inline
                    ? `resources-toc-inline-${s.id}`
                    : `resources-toc-${s.id}`
                }
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto",
                  alignItems: "center",
                  padding: "10px 0",
                  color: isActive ? "#1B1F4B" : "#3D3D55",
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontSize: 13.5,
                  fontWeight: isActive ? 700 : 500,
                  textDecoration: "none",
                  borderLeft: isActive ? "2px solid #B14E2F" : "2px solid transparent",
                  paddingLeft: 10,
                  transition: "color 160ms ease, border-color 160ms ease",
                }}
              >
                <span>{s.label}</span>
                <span
                  className="tabular-nums"
                  style={{
                    color: "#8F8DA0",
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  {s.minutes}m
                </span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Section primitives                                                      */
/* ─────────────────────────────────────────────────────────────────────── */

function SectionHeading({ id, eyebrow, minutes, title, icon: Icon, tone = "indigo" }) {
  const accent = tone === "terracotta"
    ? "#8F3C23"
    : tone === "sage"
      ? "#3A4A35"
      : "#1B1F4B";
  const iconBg = tone === "terracotta"
    ? "rgba(177,78,47,0.10)"
    : tone === "sage"
      ? "rgba(79,97,73,0.10)"
      : "rgba(27,31,75,0.06)";
  return (
    <div className="flex items-start gap-4" style={{ marginBottom: 16 }}>
      {Icon && (
        <div
          aria-hidden
          style={{
            width: 44,
            height: 44,
            borderRadius: 4,
            background: iconBg,
            color: accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={20} />
        </div>
      )}
      <div className="min-w-0">
        <div className="flex items-center gap-3">
          <span className="wn-label" style={{ color: accent }}>
            {eyebrow}
          </span>
          <span
            className="inline-flex items-center gap-1 tabular-nums"
            style={{
              fontSize: 11,
              color: "#6C6B85",
              fontWeight: 600,
            }}
          >
            <Clock size={10} /> {minutes} min read
          </span>
        </div>
        <h2
          id={id}
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontSize: "clamp(26px, 3.2vw, 34px)",
            fontWeight: 600,
            color: "#1B1F4B",
            marginTop: 4,
            scrollMarginTop: 96,
          }}
          data-testid={`resources-section-${id}-title`}
        >
          {title}
        </h2>
      </div>
    </div>
  );
}

function Divider() {
  return (
    <div
      aria-hidden
      style={{
        borderTop: "1px solid #D9D8E4",
        margin: "64px 0",
      }}
    />
  );
}

function Body({ children, max = 720 }) {
  return (
    <div
      style={{
        fontSize: 15.5,
        color: "#272636",
        lineHeight: 1.65,
        maxWidth: max,
      }}
    >
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Section 1 · Owners & CDCs                                               */
/* ─────────────────────────────────────────────────────────────────────── */

function SectionOwners({ onCta }) {
  return (
    <section data-testid="resources-section-owners">
      <SectionHeading
        id="owners"
        eyebrow="Audience"
        minutes={5}
        title="For Owners & CDCs."
        icon={Hammer}
        tone="terracotta"
      />
      <Body>
        <p>
          WHOLE Neighborhoods is a public marketplace of pre-reviewed
          single-family, duplex, and ADU designs matched to vacant
          Indianapolis parcels. Every design has already cleared a
          structured city review — so when you choose one, you skip
          months of plan review.
        </p>
      </Body>

      <div className="mt-7">
        <div className="wn-label" style={{ color: "#8F3C23" }}>
          The three-step path
        </div>
        <ol
          className="mt-3 grid gap-4 md:grid-cols-3"
          style={{ listStyle: "none", padding: 0 }}
        >
          {[
            {
              n: "01",
              t: "Browse the marketplace",
              d: "Filter by neighborhood, typology, square footage, and cost. Save designs you like.",
              cta: { to: "/marketplace", label: "Open the marketplace" },
            },
            {
              n: "02",
              t: "Request a consultation",
              d: "Send a brief to a verified architect with a parcel attached. They reply with feasibility + a fixed fee.",
              cta: { to: "/architects", label: "Browse architects" },
            },
            {
              n: "03",
              t: "Move to expedited review",
              d: "Architect files the parcel-specific drawings with the City. Pre-reviewed designs typically clear in 14 days vs. 90+.",
              cta: null,
            },
          ].map((step) => (
            <li
              key={step.n}
              className="wn-card"
              style={{ padding: 18, height: "100%" }}
              data-testid={`resources-owners-step-${step.n}`}
            >
              <div
                className="tabular-nums wn-label"
                style={{ color: "#B14E2F" }}
              >
                {step.n}
              </div>
              <div
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 600,
                  fontSize: 17,
                  color: "#1B1F4B",
                  marginTop: 4,
                  lineHeight: 1.3,
                }}
              >
                {step.t}
              </div>
              <p
                style={{
                  color: "#3D3D55",
                  fontSize: 13.5,
                  lineHeight: 1.55,
                  marginTop: 8,
                }}
              >
                {step.d}
              </p>
              {step.cta && (
                <Link
                  to={step.cta.to}
                  className="inline-flex items-center mt-3"
                  style={{
                    color: "#1B1F4B",
                    fontWeight: 700,
                    fontSize: 13,
                    textDecoration: "none",
                    gap: 4,
                  }}
                >
                  {step.cta.label} <ChevronRight size={13} />
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>

      <div
        className="mt-9 grid gap-8 md:grid-cols-2"
        data-testid="resources-owners-money-time"
      >
        <NumberCard
          eyebrow="Typical cost"
          number="$8.4k – $24k"
          unit="design license"
          note="One-time fee paid to the architect for the right to build their pre-reviewed design on one parcel. Larger typologies (duplex, multi-family) sit at the upper end."
        />
        <NumberCard
          eyebrow="Typical timeline"
          number="14"
          unit="days to expedited approval"
          note="Compared with 90+ days for a non-pre-reviewed submission. Construction inspection windows are unchanged — they live in Accela."
        />
      </div>

      <h3
        className="mt-12"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: 22,
          fontWeight: 600,
          color: "#1B1F4B",
        }}
      >
        Owner FAQ.
      </h3>
      <FaqList
        testidPrefix="resources-owners-faq"
        items={[
          {
            q: "Who actually owns the design?",
            a: "The architect owns the design. You buy a single-parcel license — the right to build it once on a specific lot. The license is non-transferable.",
          },
          {
            q: "Can I customize the design?",
            a: "Light customization (finishes, fixtures, paint) is between you and the architect. Structural or massing changes invalidate the pre-review and require a full re-submission to the City.",
          },
          {
            q: "Who pays the architect — and when?",
            a: "You pay through WHOLE. 30% on consultation acceptance, 70% on parcel-specific drawings delivered. WHOLE holds funds and releases them on milestones.",
          },
          {
            q: "What if I already own the parcel?",
            a: "Even better. Bring your address into the consultation request — the architect will check it against the catalog and propose 1–2 fits.",
          },
          {
            q: "What happens during construction?",
            a: "Standard City inspections via Accela. WHOLE displays inspection state on your project page so you don't have to log into Accela separately.",
          },
        ]}
      />

      <div
        className="mt-9 wn-card flex flex-wrap items-center gap-4"
        style={{ padding: 18, maxWidth: 720, background: "#FBFAFD" }}
      >
        <div className="min-w-0 flex-1">
          <div className="wn-label">Ready to start?</div>
          <div
            className="mt-1"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 16,
              color: "#1B1F4B",
            }}
          >
            Browse the catalog or sign in as an Owner / CDC.
          </div>
        </div>
        <Link
          to="/marketplace"
          className="wn-btn wn-btn-primary"
          data-testid="resources-owners-marketplace-cta"
          style={{ fontSize: 13 }}
        >
          Open marketplace <ArrowRight size={13} />
        </Link>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Section 2 · Architects                                                  */
/* ─────────────────────────────────────────────────────────────────────── */

function SectionArchitects({ onCta }) {
  return (
    <section data-testid="resources-section-architects">
      <SectionHeading
        id="architects"
        eyebrow="Audience"
        minutes={6}
        title="For Architects."
        icon={Compass}
        tone="indigo"
      />
      <Body>
        <p>
          WHOLE Neighborhoods is a marketplace for licensed architects who
          want to build durable infill homes at neighborhood scale. You
          submit pre-reviewed designs once, list them publicly, and the
          City does plan-review work upfront — so each homeowner contract
          is fast, predictable, and parcel-specific.
        </p>
      </Body>

      <div className="mt-8">
        <div className="wn-label">Application & approval</div>
        <ol
          className="mt-3 grid gap-3 md:grid-cols-3"
          style={{ listStyle: "none", padding: 0 }}
        >
          {[
            {
              n: "Step 01",
              t: "About you & your firm",
              d: "Headshot, license number, firm name, tagline, year founded, team size.",
            },
            {
              n: "Step 02",
              t: "Practice",
              d: "Service areas, typology specialisations, practice statement (≥300 chars), biography, lifestyle focuses.",
            },
            {
              n: "Step 03",
              t: "Credentials & documents",
              d: "AIA / NCARB / LEED credentials, license certificate, E&O insurance, optional portfolio samples.",
            },
          ].map((s) => (
            <li
              key={s.n}
              className="wn-card"
              style={{ padding: 16 }}
              data-testid={`resources-arch-app-${s.n.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="wn-label" style={{ color: "#B14E2F" }}>{s.n}</div>
              <div
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 600,
                  fontSize: 15.5,
                  color: "#1B1F4B",
                  marginTop: 4,
                }}
              >
                {s.t}
              </div>
              <p style={{ color: "#3D3D55", fontSize: 13, lineHeight: 1.55, marginTop: 6 }}>
                {s.d}
              </p>
            </li>
          ))}
        </ol>
        <p
          style={{
            color: "#6C6B85",
            fontSize: 12.5,
            marginTop: 8,
            fontStyle: "italic",
            fontFamily: '"Newsreader", Georgia, serif',
          }}
        >
          Median time to approval: 3 business days, end-to-end.
        </p>
      </div>

      <h3
        className="mt-10"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: 22,
          fontWeight: 600,
          color: "#1B1F4B",
        }}
      >
        What we look for in approval.
      </h3>
      <Body>
        <ul style={{ paddingLeft: 18, marginTop: 8 }}>
          <li><strong>Active licensure</strong> in Indiana, verifiable against state records.</li>
          <li><strong>Current E&O insurance</strong> covering residential design at ≥ $1M.</li>
          <li><strong>Demonstrated infill experience</strong> — at least one residential project on a lot ≤ 5,000 sq ft within the last 5 years.</li>
          <li><strong>A coherent practice statement</strong> that names a stance, not a service list.</li>
          <li><strong>No active license discipline</strong> in any state.</li>
        </ul>
      </Body>

      <h3
        className="mt-10"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: 22,
          fontWeight: 600,
          color: "#1B1F4B",
        }}
      >
        Design submission checklist.
      </h3>
      <Checklist
        testidPrefix="resources-arch-checklist"
        items={[
          "Site plan with setbacks, drives, and easements (1:200 or 1:100)",
          "Floor plans for every level (1:50)",
          "Two elevations, minimum (front + one side)",
          "Section through the longest dimension",
          "Material schedule with manufacturer-grade SKUs where applicable",
          "Energy compliance narrative (Indiana Residential Code Ch. 11)",
          "Three renders: street view, interior living area, kitchen",
          "Cost narrative: target build cost ± 12%",
          "Six pre-approved typology classification (single_family / duplex / adu / multi_family)",
          "Self-attestation: structural, mechanical, and plumbing scopes are within your license envelope or stamped by a partner",
        ]}
      />

      <h3
        className="mt-10"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: 22,
          fontWeight: 600,
          color: "#1B1F4B",
        }}
      >
        How the City reviews.
      </h3>
      <Body>
        <p>
          City Staff at the Department of Metropolitan Development (DMD)
          review pre-reviewed candidates through Accela — the City's
          system of record. WHOLE is observational; Accela governs.
          Reviewers check four classes of compliance:
        </p>
        <ol style={{ paddingLeft: 18, marginTop: 8 }}>
          <li><strong>Zoning</strong> — typology against the parcel's overlay (front yard, side yard, lot coverage, height, FAR).</li>
          <li><strong>Egress & life safety</strong> — IRC chapters R310 / R311.</li>
          <li><strong>Energy</strong> — Indiana Residential Code Ch. 11.</li>
          <li><strong>Form-based overlays</strong> — Mass Ave, Mile Square, and historic district guidelines where applicable.</li>
        </ol>
      </Body>

      <h3
        className="mt-10"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: 22,
          fontWeight: 600,
          color: "#1B1F4B",
        }}
      >
        Revenue & payment.
      </h3>
      <Body>
        <p>
          You set the per-parcel license fee for each design (typical
          range $8k – $24k). WHOLE charges a transparent <strong>10%
          platform fee</strong> on the license, plus a flat $250
          consultation handling fee per matched consultation. Funds are
          held in escrow and released on milestone:
        </p>
        <ul style={{ paddingLeft: 18, marginTop: 8 }}>
          <li><strong>30%</strong> when the owner accepts the consultation.</li>
          <li><strong>70%</strong> when parcel-specific drawings are delivered to Accela.</li>
        </ul>
        <p style={{ marginTop: 10 }}>
          Receivables show up on <code>/architect/payments</code> with a per-line breakdown
          and downloadable PDF receipts.
        </p>
      </Body>

      <div
        className="mt-9 wn-card flex flex-wrap items-center gap-4"
        style={{ padding: 18, maxWidth: 720, background: "#FBFAFD" }}
      >
        <div className="min-w-0 flex-1">
          <div className="wn-label">Apply to the network</div>
          <div
            className="mt-1"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 16,
              color: "#1B1F4B",
            }}
          >
            Three steps, roughly 25 minutes.
          </div>
        </div>
        <Link
          to="/register/architect"
          className="wn-btn wn-btn-primary"
          data-testid="resources-arch-apply-cta"
          style={{ fontSize: 13 }}
        >
          Start application <ArrowRight size={13} />
        </Link>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Section 3 · City Staff                                                  */
/* ─────────────────────────────────────────────────────────────────────── */

function SectionCity() {
  return (
    <section data-testid="resources-section-city">
      <SectionHeading
        id="city"
        eyebrow="Audience"
        minutes={4}
        title="For City Staff."
        icon={Landmark}
        tone="indigo"
      />
      <Body>
        <p>
          <strong>Accela governs. WHOLE observes.</strong> WHOLE
          Neighborhoods does not write to your records. It mirrors
          relevant signals — submission counts, review queues,
          inspection states — into a single editorial surface so DMD
          staff can spot trends without leaving the system of record.
        </p>
      </Body>

      <h3
        className="mt-9"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: 22,
          fontWeight: 600,
          color: "#1B1F4B",
        }}
      >
        How the Accela sync works.
      </h3>
      <Body>
        <p>
          A scheduled sync polls Accela every 15 minutes for cases in any
          stage between <code>Submitted</code> and <code>Issued</code>.
          The sync writes only into WHOLE's read replica:
        </p>
        <ul style={{ paddingLeft: 18, marginTop: 8 }}>
          <li>Case ID and current stage</li>
          <li>Parcel reference</li>
          <li>Linked design (if any)</li>
          <li>Reviewer notes that DMD has marked public</li>
        </ul>
        <p style={{ marginTop: 10 }}>
          The Sync state is displayed on every case row: <em>Synced</em>,{" "}
          <em>Pending sync</em>, or <em>Sync error</em>. Errors are routed
          to <code>devops@wholeneighborhoods.org</code> and rarely block
          governance.
        </p>
      </Body>

      <h3
        className="mt-9"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: 22,
          fontWeight: 600,
          color: "#1B1F4B",
        }}
      >
        Review SLAs.
      </h3>
      <div
        className="mt-3 grid gap-3 md:grid-cols-3"
        data-testid="resources-city-slas"
      >
        {[
          { label: "Standard review", value: "90 days", note: "Conventional residential submittal." },
          { label: "Expedited (pre-reviewed)", value: "14 days", note: "Matched against a pre-reviewed design." },
          { label: "RFI response window", value: "10 days", note: "Owner / architect response before stale." },
        ].map((s) => (
          <div key={s.label} className="wn-card" style={{ padding: 16 }}>
            <div className="wn-label">{s.label}</div>
            <div
              className="tabular-nums"
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontSize: 26,
                fontWeight: 600,
                color: "#1B1F4B",
                marginTop: 4,
                lineHeight: 1.1,
              }}
            >
              {s.value}
            </div>
            <p style={{ color: "#6C6B85", fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>
              {s.note}
            </p>
          </div>
        ))}
      </div>

      <h3
        className="mt-9"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: 22,
          fontWeight: 600,
          color: "#1B1F4B",
        }}
      >
        The six pre-approved typologies.
      </h3>
      <Body>
        <p>
          WHOLE's catalog is intentionally narrow. Six typologies cover
          ~92% of Indianapolis infill demand and let DMD invest review
          time once per typology, not once per project.
        </p>
      </Body>
      <div
        className="mt-4 grid gap-2 md:grid-cols-2"
        style={{ maxWidth: 720 }}
        data-testid="resources-city-typologies"
      >
        {[
          { k: "single_family", label: "Single-family detached", w: "Modest 2-3 BR on standard 50' lot" },
          { k: "duplex_stack", label: "Stacked duplex", w: "Two units, one upper, one lower" },
          { k: "duplex_side", label: "Side-by-side duplex", w: "Two units sharing a center wall" },
          { k: "adu_detached", label: "Detached ADU", w: "Backyard cottage, ≤ 800 sq ft" },
          { k: "adu_attached", label: "Attached ADU", w: "Above garage or in-law suite" },
          { k: "small_multi", label: "Small multi-family", w: "3-4 units on combined parcels" },
        ].map((t) => (
          <div
            key={t.k}
            className="flex items-start gap-3"
            style={{ padding: "10px 0", borderBottom: "1px solid #E8E7EE" }}
          >
            <Building2 size={14} style={{ marginTop: 2, color: "#8F3C23", flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#1B1F4B" }}>{t.label}</div>
              <div
                style={{
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontStyle: "italic",
                  color: "#6C6B85",
                  fontSize: 12.5,
                }}
              >
                {t.w}
              </div>
            </div>
          </div>
        ))}
      </div>

      <h3
        className="mt-9"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: 22,
          fontWeight: 600,
          color: "#1B1F4B",
        }}
      >
        Recording a code change.
      </h3>
      <Body>
        <p>
          When DMD or City Council passes an ordinance that affects
          pre-reviewed designs (height limits, lot coverage, energy
          targets), it must be recorded on WHOLE so affected designs are
          flagged for resubmission. Platform Admin records the change at{" "}
          <code>/admin/code-changes</code>. WHOLE then notifies every
          architect whose designs intersect the change.
        </p>
      </Body>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* ─────────────────────────────────────────────────────────────────────── */
/* Section 3b · Watch                                                       */
/*                                                                          */
/* Placeholder IA for the video field guide. Tiles render as schematic      */
/* line-drawing thumbnails (no actual video files yet). Each track          */
/* sits horizontally; tiles scroll horizontally on narrow screens. When     */
/* real videos exist, we replace the placeholder thumb with a YouTube      */
/* embed (or thumb + lightbox) and flip the "Coming soon" pill to the      */
/* video duration.                                                          */
/* ─────────────────────────────────────────────────────────────────────── */



function SectionWatch() {
  return (
    <div data-testid="resources-section-watch">
      <SectionHeading
        id="watch"
        eyebrow="Field guide · video"
        minutes={12}
        title="Watch."
        tone="sage"
      />

      <Body>
        A short video library is in production. The civic literacy
        track will live publicly on YouTube — useful to anyone in
        Indianapolis trying to make sense of pre-reviewed housing,
        whether they end up on this platform or not. The Owners &
        CDCs and Architects tracks will live here, embedded
        alongside the relevant Reference Book chapters.
      </Body>

      <div className="mt-10 space-y-12">
        {WATCH_TRACKS.map((track) => (
          <WatchTrack key={track.id} track={track} />
        ))}
      </div>

      <div
        className="mt-10"
        style={{
          padding: "12px 16px",
          border: "1px solid #DDD9CE",
          borderLeft: "3px solid #4F6149",
          borderRadius: 4,
          background: "#F4F2EC",
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          fontSize: 13,
          color: "#4F6149",
          lineHeight: 1.55,
          maxWidth: 720,
        }}
        data-testid="resources-watch-rationale"
      >
        Why YouTube and not self-hosted: the civic-literacy track is
        only useful if it&rsquo;s discoverable. Pre-reviewed housing is a
        public-good idea — locking the explainer behind a sign-in
        would defeat the point. Embedded back into this Reference
        Book gives the in-platform reader the editorial framing
        WHOLE adds around each video.
      </div>
    </div>
  );
}


function WatchTrack({ track }) {
  return (
    <div data-testid={`resources-watch-track-${track.id}`}>
      <div className="flex items-baseline" style={{ gap: 14 }}>
        <span
          className="wn-label"
          style={{ color: "#8F3C23", letterSpacing: "0.08em" }}
        >
          {track.eyebrow}
        </span>
        <span
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          {track.sub}
        </span>
      </div>

      <div
        className="mt-4"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gap: 22,
        }}
      >
        {track.tiles.map((t) => (
          <WatchTile key={t.slug} tile={t} />
        ))}
      </div>
    </div>
  );
}


function WatchTile({ tile }) {
  const status = tile.status === "first_release"
    ? {
        label: "First release · Q2",
        bg: "#E8EAD9",
        border: "#B8BFA0",
        ink: "#3A4A35",
      }
    : {
        label: "Coming soon",
        bg: "#F4F2EC",
        border: "#DDD9CE",
        ink: "#6C6B85",
      };
  return (
    <div
      data-testid={`resources-watch-tile-${tile.slug}`}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
      }}
    >
      {/* Schematic thumbnail — sage line-drawing placeholder until
          the real video lands. */}
      <div
        aria-hidden
        style={{
          position: "relative",
          aspectRatio: "16 / 9",
          background: "#F4F2EC",
          border: "1px solid #DDD9CE",
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        {/* SVG schematic film-frame */}
        <svg
          viewBox="0 0 160 90"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid meet"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        >
          {/* Faint baseline grid */}
          <g stroke="#DDD9CE" strokeWidth="0.4" fill="none">
            <line x1="0" y1="22.5" x2="160" y2="22.5" />
            <line x1="0" y1="45"   x2="160" y2="45"   />
            <line x1="0" y1="67.5" x2="160" y2="67.5" />
          </g>
          {/* Sage play triangle */}
          <g transform="translate(80,45)">
            <circle r="13" fill="#FBFAFD" stroke="#B8BFA0" strokeWidth="0.8" />
            <polygon
              points="-4,-6 -4,6 6,0"
              fill="#3A4A35"
            />
          </g>
        </svg>
        {/* Duration pill, bottom-right */}
        <span
          className="tabular-nums"
          style={{
            position: "absolute",
            right: 8,
            bottom: 8,
            padding: "2px 8px",
            borderRadius: 3,
            background: "rgba(27,31,75,0.92)",
            color: "#FBFAFD",
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 700,
            fontSize: 10.5,
            letterSpacing: "0.06em",
          }}
        >
          {tile.duration}
        </span>
        {/* Status pill, top-left */}
        <span
          style={{
            position: "absolute",
            left: 8,
            top: 8,
            padding: "2px 8px",
            borderRadius: 3,
            background: status.bg,
            color: status.ink,
            border: `1px solid ${status.border}`,
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 700,
            fontSize: 9.5,
            letterSpacing: "0.10em",
            textTransform: "uppercase",
          }}
        >
          {status.label}
        </span>
      </div>

      <h4
        className="mt-3"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 500,
          fontSize: 17,
          color: "#1B1F4B",
          lineHeight: 1.3,
          margin: 0,
          letterSpacing: "-0.005em",
        }}
      >
        {tile.title}
      </h4>
      <p
        className="mt-2"
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#6C6B85",
          fontSize: 13,
          lineHeight: 1.55,
          margin: 0,
        }}
      >
        {tile.body}
      </p>
    </div>
  );
}



/* Section 4 · Glossary                                                    */
/* ─────────────────────────────────────────────────────────────────────── */

function SectionGlossary() {
  const TERMS = [
    {
      t: "Pre-reviewed design",
      d: "An architectural design that has already cleared a structured city review against zoning, life-safety, and energy code. Buying a license lets you skip 80% of plan review.",
    },
    {
      t: "Expedited review",
      d: "Accela's 14-day track for permits using a pre-reviewed design on a verified parcel.",
    },
    {
      t: "Accela case",
      d: "The City of Indianapolis's permit record. The system of truth for every project. WHOLE mirrors but never overwrites.",
    },
    {
      t: "Overlay district",
      d: "A zoning layer that sits on top of base zoning with additional rules — historic, form-based, environmental, riverfront.",
    },
    {
      t: "Code change",
      d: "A formally adopted ordinance or rule change that retroactively affects already-approved designs. Recorded on WHOLE so designs can be flagged or revised.",
    },
    {
      t: "RFI",
      d: "Request for Information. A reviewer's question that pauses the review clock until the architect or owner responds.",
    },
    {
      t: "Consultation",
      d: "The owner's first formal contact with an architect on WHOLE — a brief, optional parcel, and a request for feasibility and fee.",
    },
    {
      t: "Permissive zoning",
      d: "A zoning regime in which a use is allowed by right (no variance, no special exception). Most WHOLE-catalogued designs target permissive zones.",
    },
    {
      t: "ADU",
      d: "Accessory Dwelling Unit. A secondary residence on a lot with a primary residence — detached cottage or attached in-law suite.",
    },
    {
      t: "Typology",
      d: "The architectural class of a design — single-family, duplex stacked, duplex side-by-side, ADU detached, ADU attached, small multi-family.",
    },
    {
      t: "Verified architect",
      d: "A WHOLE network member whose license, E&O insurance, and infill credentials have been confirmed by Platform Admin.",
    },
    {
      t: "E&O insurance",
      d: "Errors & Omissions professional liability. Architects must carry at least $1M residential coverage to publish on WHOLE.",
    },
    {
      t: "Compliance preview",
      d: "A pre-submittal compliance scorecard generated by WHOLE that shows likely zoning, life-safety, and energy hits before drawings reach DMD.",
    },
    {
      t: "MBE / WBE / VBE",
      d: "Minority / Women / Veteran Business Enterprise. Self-attested flags on architect profiles to surface diverse firms in match-making.",
    },
    {
      t: "CDC",
      d: "Community Development Corporation. Non-profit developer working block-by-block in a defined neighborhood — a core WHOLE customer.",
    },
  ];

  return (
    <section data-testid="resources-section-glossary">
      <SectionHeading
        id="glossary"
        eyebrow="Reference"
        minutes={3}
        title="Civic glossary."
        icon={BookOpen}
      />
      <Body>
        <p>
          Fifteen terms used across the platform. If you only read one
          section before a stakeholder meeting, read this one.
        </p>
      </Body>
      <dl
        className="mt-6 grid gap-x-10 gap-y-5"
        style={{ gridTemplateColumns: "minmax(180px, 220px) 1fr", maxWidth: 860 }}
        data-testid="resources-glossary-list"
      >
        {TERMS.map((term) => (
          <React.Fragment key={term.t}>
            <dt
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 15,
                color: "#1B1F4B",
                paddingTop: 2,
              }}
              data-testid={`resources-glossary-term-${slug(term.t)}`}
            >
              {term.t}
            </dt>
            <dd
              style={{
                color: "#3D3D55",
                fontSize: 14,
                lineHeight: 1.6,
                margin: 0,
              }}
            >
              {term.d}
            </dd>
          </React.Fragment>
        ))}
      </dl>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Section 5 · Code & Standards                                            */
/* ─────────────────────────────────────────────────────────────────────── */

function SectionCode() {
  const items = [
    {
      sec: "740-403",
      name: "Residential infill — base setbacks",
      lines: [
        "Front yard: 15 ft minimum, averaged with two adjacent lots.",
        "Side yards: 3 ft (one-story) / 5 ft (two-story).",
        "Rear yard: 20 ft (single-family) / 30 ft (ADU separation).",
      ],
    },
    {
      sec: "740-407",
      name: "Lot coverage & FAR",
      lines: [
        "Lot coverage: 45% (D-5), 55% (D-5 II), 65% (D-8).",
        "Floor Area Ratio: 0.6 base, 0.9 with overlay bonus.",
      ],
    },
    {
      sec: "740-505",
      name: "Height limits",
      lines: [
        "Base: 35 ft to mid-roof.",
        "Mass Ave + historic overlays: 28 ft, contextual averaging required.",
      ],
    },
    {
      sec: "744-203",
      name: "ADU rules",
      lines: [
        "Max 800 sq ft net living area.",
        "One off-street parking space required per ADU (waivable in TOD zones).",
        "Owner-occupancy of either unit required for 5 years post-CO.",
      ],
    },
    {
      sec: "IRC Ch. 11 (Indiana)",
      name: "Energy compliance",
      lines: [
        "Prescriptive path: Climate Zone 5A, R-49 attic, R-20 walls, R-30 floor.",
        "Performance path: HERS ≤ 65 or modelled equivalent.",
      ],
    },
    {
      sec: "IRC R310 / R311",
      name: "Egress & life safety",
      lines: [
        "Every sleeping room: one emergency escape window (5.7 sq ft clear net).",
        "Stair rise ≤ 7¾ in; tread ≥ 10 in.",
      ],
    },
  ];
  return (
    <section data-testid="resources-section-code">
      <SectionHeading
        id="code"
        eyebrow="Reference"
        minutes={4}
        title="Code & standards."
        icon={ShieldCheck}
      />
      <Body>
        <p>
          A summary of the Indianapolis Revised Code and Indiana
          Residential Code sections most often invoked during WHOLE
          design reviews. These are summaries — always defer to the
          current code text at{" "}
          <a
            href="https://library.municode.com/in/indianapolis_-_marion_county"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#1B1F4B", fontWeight: 700 }}
            data-testid="resources-code-municode-link"
          >
            municode.com <ArrowUpRight size={11} style={{ display: "inline" }} />
          </a>
          .
        </p>
      </Body>

      <div
        className="mt-6"
        style={{
          border: "1px solid #D9D8E4",
          borderRadius: 4,
          overflow: "hidden",
          background: "#FBFAFD",
        }}
        data-testid="resources-code-table"
      >
        {items.map((it, i) => (
          <div
            key={it.sec}
            className="grid gap-4"
            style={{
              gridTemplateColumns: "160px 1fr",
              padding: "16px 20px",
              borderBottom: i < items.length - 1 ? "1px solid #E8E7EE" : "none",
              alignItems: "start",
            }}
          >
            <div className="tabular-nums" style={{ color: "#8F3C23", fontWeight: 700, fontSize: 13 }}>
              §&nbsp;{it.sec}
            </div>
            <div>
              <div
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 600,
                  fontSize: 15.5,
                  color: "#1B1F4B",
                }}
              >
                {it.name}
              </div>
              <ul
                style={{
                  marginTop: 6,
                  paddingLeft: 18,
                  color: "#3D3D55",
                  fontSize: 13.5,
                  lineHeight: 1.6,
                }}
              >
                {it.lines.map((l, j) => (
                  <li key={j}>{l}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <p
        style={{
          color: "#6C6B85",
          fontSize: 12,
          marginTop: 8,
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
        }}
        data-testid="resources-code-last-reviewed"
      >
        Last reviewed: February 2026.
      </p>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Section 6 · Downloads                                                   */
/* ─────────────────────────────────────────────────────────────────────── */

function SectionDownloads() {
  const items = [
    {
      title: "Letter of intent template",
      who: "Owner → Architect",
      desc: "Pre-filled brief covering parcel, budget, family, timeline.",
      type: "PDF",
      key: "loi",
    },
    {
      title: "Sample project budget worksheet",
      who: "Owner",
      desc: "12-line build-cost worksheet with Indianapolis local benchmarks.",
      type: "CSV",
      key: "budget",
    },
    {
      title: "Design submission checklist",
      who: "Architect",
      desc: "The ten-item drawing-set checklist used by City Staff.",
      type: "PDF",
      key: "submit",
    },
    {
      title: "Consultation prep checklist",
      who: "Owner",
      desc: "What to bring to your first 30-minute architect call.",
      type: "PDF",
      key: "consult",
    },
  ];
  return (
    <section data-testid="resources-section-downloads">
      <SectionHeading
        id="downloads"
        eyebrow="Reference"
        minutes={1}
        title="Downloads & templates."
        icon={Download}
      />
      <Body>
        <p>
          Working documents that match the WHOLE process exactly. Bring
          them into your first call with an architect or your first DMD
          pre-submittal meeting.
        </p>
      </Body>
      <div
        className="mt-6 grid gap-3 md:grid-cols-2"
        style={{ maxWidth: 860 }}
        data-testid="resources-downloads-grid"
      >
        {items.map((it) => (
          <a
            key={it.key}
            href={`mailto:hello@wholeneighborhoods.org?subject=${encodeURIComponent(
              "Request: " + it.title,
            )}`}
            className="wn-card flex items-start gap-3"
            data-testid={`resources-download-${it.key}`}
            style={{
              padding: 16,
              textDecoration: "none",
              color: "inherit",
              transition: "background-color 160ms ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#FBFAFD")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <div
              aria-hidden
              style={{
                width: 36,
                height: 36,
                borderRadius: 4,
                background: "rgba(177,78,47,0.10)",
                color: "#8F3C23",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <FileText size={17} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 600,
                    fontSize: 15,
                    color: "#1B1F4B",
                    lineHeight: 1.3,
                  }}
                >
                  {it.title}
                </div>
                <span
                  className="tabular-nums"
                  style={{
                    fontSize: 10,
                    padding: "1px 6px",
                    border: "1px solid #D9D8E4",
                    borderRadius: 999,
                    color: "#6C6B85",
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                  }}
                >
                  {it.type}
                </span>
              </div>
              <div
                className="wn-label"
                style={{ color: "#8F3C23", marginTop: 4 }}
              >
                {it.who}
              </div>
              <p style={{ color: "#3D3D55", fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>
                {it.desc}
              </p>
            </div>
            <Download size={14} style={{ color: "#6C6B85", flexShrink: 0, marginTop: 4 }} />
          </a>
        ))}
      </div>
      <p
        style={{
          color: "#6C6B85",
          fontSize: 12,
          marginTop: 12,
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
        }}
      >
        These templates are emailed on request — click any tile and your
        mail client opens a pre-addressed message to{" "}
        <code>hello@wholeneighborhoods.org</code>.
      </p>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Section 7 · Help & Contact                                              */
/* ─────────────────────────────────────────────────────────────────────── */

function SectionHelp() {
  const lanes = [
    {
      who: "Owners & CDCs",
      email: "owners@wholeneighborhoods.org",
      blurb: "Help choosing a design, parcel, or architect.",
      testid: "resources-help-owners",
    },
    {
      who: "Architects",
      email: "architects@wholeneighborhoods.org",
      blurb: "Application, profile, design publication, payouts.",
      testid: "resources-help-architects",
    },
    {
      who: "City Staff",
      email: "city@wholeneighborhoods.org",
      blurb: "Accela sync, reporting, code change recording.",
      testid: "resources-help-city",
    },
    {
      who: "Partnerships & demo",
      email: "partnerships@wholeneighborhoods.org",
      blurb: "City, CDC, philanthropy, and press inquiries.",
      testid: "resources-help-partners",
    },
  ];
  return (
    <section data-testid="resources-section-help">
      <SectionHeading
        id="help"
        eyebrow="Reference"
        minutes={1}
        title="Help & contact."
        icon={Mail}
      />
      <Body>
        <p>
          Four lanes — owners, architects, city, partnerships. Use the
          right one and you'll usually hear back the same business day.
        </p>
      </Body>
      <div
        className="mt-6 grid gap-3 md:grid-cols-2"
        style={{ maxWidth: 860 }}
      >
        {lanes.map((l) => (
          <a
            key={l.email}
            href={`mailto:${l.email}`}
            className="wn-card flex items-start gap-3"
            data-testid={l.testid}
            style={{
              padding: 16,
              textDecoration: "none",
              color: "inherit",
              transition: "background-color 160ms ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#FBFAFD")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <div
              aria-hidden
              style={{
                width: 36,
                height: 36,
                borderRadius: 4,
                background: "rgba(27,31,75,0.06)",
                color: "#1B1F4B",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Mail size={17} />
            </div>
            <div className="min-w-0 flex-1">
              <div
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 600,
                  fontSize: 15,
                  color: "#1B1F4B",
                }}
              >
                {l.who}
              </div>
              <p style={{ color: "#3D3D55", fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>
                {l.blurb}
              </p>
              <div
                className="tabular-nums"
                style={{
                  color: "#8F3C23",
                  fontSize: 12.5,
                  fontWeight: 700,
                  marginTop: 6,
                }}
              >
                {l.email}
              </div>
            </div>
            <ArrowUpRight size={14} style={{ color: "#6C6B85", flexShrink: 0, marginTop: 4 }} />
          </a>
        ))}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────── */
/* Bits                                                                    */
/* ─────────────────────────────────────────────────────────────────────── */

function NumberCard({ eyebrow, number, unit, note }) {
  return (
    <div
      className="wn-card"
      style={{ padding: 22 }}
    >
      <div className="wn-label" style={{ color: "#8F3C23" }}>
        {eyebrow}
      </div>
      <div
        className="tabular-nums mt-2"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: 38,
          fontWeight: 600,
          color: "#1B1F4B",
          lineHeight: 1.05,
        }}
      >
        {number}
      </div>
      <div
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#6C6B85",
          fontSize: 13,
          marginTop: 2,
        }}
      >
        {unit}
      </div>
      <p style={{ color: "#3D3D55", fontSize: 13.5, lineHeight: 1.6, marginTop: 10 }}>
        {note}
      </p>
    </div>
  );
}

function FaqList({ items, testidPrefix }) {
  const [open, setOpen] = useState(0);
  return (
    <div
      className="mt-4"
      style={{
        border: "1px solid #D9D8E4",
        borderRadius: 4,
        overflow: "hidden",
        background: "#FBFAFD",
        maxWidth: 720,
      }}
      data-testid={`${testidPrefix}-list`}
    >
      {items.map((it, i) => {
        const isOpen = open === i;
        return (
          <div
            key={i}
            style={{ borderBottom: i < items.length - 1 ? "1px solid #E8E7EE" : "none" }}
          >
            <button
              type="button"
              onClick={() => setOpen(isOpen ? -1 : i)}
              className="text-left"
              style={{
                width: "100%",
                background: "transparent",
                border: "none",
                padding: "14px 18px",
                cursor: "pointer",
                display: "grid",
                gridTemplateColumns: "1fr auto",
                alignItems: "center",
                gap: 12,
              }}
              data-testid={`${testidPrefix}-${i}-question`}
              aria-expanded={isOpen}
            >
              <span
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 600,
                  fontSize: 15,
                  color: "#1B1F4B",
                }}
              >
                {it.q}
              </span>
              <ChevronRight
                size={15}
                style={{
                  color: "#6C6B85",
                  transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                  transition: "transform 160ms ease",
                }}
              />
            </button>
            {isOpen && (
              <div
                style={{
                  padding: "0 18px 16px",
                  color: "#3D3D55",
                  fontSize: 14,
                  lineHeight: 1.6,
                }}
                data-testid={`${testidPrefix}-${i}-answer`}
              >
                {it.a}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Checklist({ items, testidPrefix }) {
  return (
    <ul
      className="mt-4 grid gap-2"
      style={{ maxWidth: 720, listStyle: "none", padding: 0 }}
      data-testid={`${testidPrefix}-list`}
    >
      {items.map((it, i) => (
        <li
          key={i}
          className="flex items-start gap-3"
          style={{
            padding: "10px 14px",
            border: "1px solid #E8E7EE",
            borderRadius: 4,
            background: "#FBFAFD",
            fontSize: 13.5,
            color: "#272636",
            lineHeight: 1.5,
          }}
        >
          <span
            aria-hidden
            style={{
              width: 18,
              height: 18,
              borderRadius: 4,
              border: "1px solid #B14E2F",
              color: "#B14E2F",
              fontSize: 10,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              marginTop: 2,
            }}
            className="tabular-nums"
          >
            ✓
          </span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function BackToTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!visible) return null;
  return (
    <button
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      data-testid="resources-back-to-top"
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 40,
        background: "#1B1F4B",
        color: "#FBFAFD",
        border: "none",
        borderRadius: 999,
        width: 44,
        height: 44,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        boxShadow: "0 4px 14px rgba(27,31,75,0.18)",
        transition: "background-color 160ms ease, transform 160ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#0F1238";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "#1B1F4B";
        e.currentTarget.style.transform = "translateY(0)";
      }}
      aria-label="Back to top"
    >
      <ChevronUp size={18} />
    </button>
  );
}

function slug(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
